import { createTheme } from "@mui/material/styles";


const theme = createTheme({
  direction: "rtl",
  palette: {
    primary: {
      main: "#d5aa2f",
      dark: "#b08c26",
      light: "#e6c76a",
      "50": "#fdf8e8",
      contrastText: "#ffffff",
    },
    secondary: {
      main: "#9a6516",
      dark: "#6f480f",
      light: "#c78b3a",
      contrastText: "#ffffff",
    },
    error: {
      main: "#d32f2f",
      light: "#ef5350",
      dark: "#c62828",
      "50": "#fbeaea",
    },
    success: {
      main: "#2e7d32",
      light: "#4caf50",
      dark: "#1b5e20",
      "50": "#edf7ed",
    },
    warning: {
      main: "#ed6c02",
      light: "#ff9800",
      dark: "#e65100",
    },
    info: {
      main: "#0288d1",
      light: "#03a9f4",
      dark: "#01579b",
    },
    background: {
      default: "#fafafa",
      paper: "#ffffff",
    },
    text: {
      primary: "#1a1a1a",
      secondary: "#6b6b6b",
      disabled: "#b0b0b0",
    },
    divider: "rgba(0, 0, 0, 0.08)",
    grey: {
      50: "#fafafa",
      100: "#f5f5f5",
      200: "#eeeeee",
      300: "#e0e0e0",
      400: "#bdbdbd",
      500: "#9e9e9e",
    },
    action: {
      hover: "rgba(213, 170, 47, 0.06)",
      selected: "rgba(213, 170, 47, 0.12)",
    },
  },
  typography: {
    fontFamily: "'Cairo', 'Inter', 'Roboto', 'Arial', sans-serif",
    h1: { fontWeight: 800, letterSpacing: "-0.02em" },
    h2: { fontWeight: 800, letterSpacing: "-0.01em" },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600, fontSize: "1rem" },
    subtitle2: { fontWeight: 600, fontSize: "0.875rem" },
    body1: { lineHeight: 1.7 },
    body2: { lineHeight: 1.6 },
    button: { fontWeight: 700, letterSpacing: "0.02em" },
    caption: { fontSize: "0.75rem", color: "#6b6b6b" },
  },
  shape: {
    borderRadius: 12,
  },
  shadows: [
    "none",
    "0 1px 3px rgba(0,0,0,0.06)",
    "0 2px 6px rgba(0,0,0,0.06)",
    "0 4px 12px rgba(0,0,0,0.08)",
    "0 6px 16px rgba(0,0,0,0.08)",
    "0 8px 24px rgba(0,0,0,0.1)",
    "0 12px 32px rgba(0,0,0,0.1)",
    "0 16px 40px rgba(0,0,0,0.12)",
    ...Array(17).fill("0 16px 40px rgba(0,0,0,0.12)"),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          scrollBehavior: "smooth",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
        },
        "*::-webkit-scrollbar": {
          width: 6,
          height: 6,
        },
        "*::-webkit-scrollbar-thumb": {
          borderRadius: 3,
          backgroundColor: "rgba(0,0,0,0.15)",
        },
        "*::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: "none",
          fontWeight: 700,
          padding: "8px 20px",
          transition: "all 0.25s ease",
        },
        contained: {
          boxShadow: "0 2px 8px rgba(213, 170, 47, 0.3)",
          "&:hover": {
            boxShadow: "0 4px 16px rgba(213, 170, 47, 0.4)",
            transform: "translateY(-1px)",
          },
          "&:active": {
            transform: "translateY(0)",
          },
        },
        outlined: {
          borderWidth: 1.5,
          "&:hover": {
            borderWidth: 1.5,
            backgroundColor: "rgba(213, 170, 47, 0.04)",
          },
        },
        text: {
          "&:hover": {
            backgroundColor: "rgba(213, 170, 47, 0.06)",
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 16,
          border: "1px solid rgba(0, 0, 0, 0.06)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            boxShadow: "0 8px 30px rgba(0, 0, 0, 0.08)",
            transform: "translateY(-2px)",
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: "none",
        },
        rounded: {
          borderRadius: 16,
        },
        outlined: {
          borderColor: "rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 600,
          fontSize: "0.8rem",
        },
        sizeSmall: {
          height: 28,
          fontSize: "0.75rem",
        },
        filled: {
          backgroundColor: "rgba(213, 170, 47, 0.1)",
          color: "#9a6516",
          "&:hover": {
            backgroundColor: "rgba(213, 170, 47, 0.18)",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 20,
          boxShadow: "0 24px 48px rgba(0, 0, 0, 0.16)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: "1.25rem",
          padding: "20px 24px 12px",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "outlined",
        size: "small",
      },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 10,
            transition: "all 0.2s ease",
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: "#d5aa2f",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: "#d5aa2f",
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(0, 0, 0, 0.06)",
          color: "#1a1a1a",
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: "64px !important",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        rounded: {
          borderRadius: 10,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          marginBottom: 2,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: "rgba(213, 170, 47, 0.06)",
          },
          "&.Mui-selected": {
            backgroundColor: "rgba(213, 170, 47, 0.1)",
            borderInlineStart: "3px solid #d5aa2f",
            "&:hover": {
              backgroundColor: "rgba(213, 170, 47, 0.15)",
            },
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: "rgba(0, 0, 0, 0.06)",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          fontWeight: 600,
        },
        standardError: {
          backgroundColor: "#fbeaea",
          color: "#c62828",
        },
        standardSuccess: {
          backgroundColor: "#edf7ed",
          color: "#1b5e20",
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        iconFilled: {
          color: "#d5aa2f",
        },
        iconHover: {
          color: "#e6c76a",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: "all 0.2s ease",
          "&:hover": {
            transform: "scale(1.05)",
          },
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 700,
          fontSize: "0.7rem",
          minWidth: 18,
          height: 18,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 10,
        },
      },
    },
  },
});

export default theme;
